import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export function useProducts(storeId: string | null, filters: Record<string, string> = {}) {
  const params = new URLSearchParams({ storeId: storeId || "", ...filters });
  return useQuery({
    queryKey: ["productsList", storeId, filters],
    queryFn: () => fetchJson<any>(`/api/products/list?${params}`),
    enabled: !!storeId,
    refetchOnWindowFocus: "always",
  });
}

export function useProduct(storeId: string | null, productId: string | null) {
  return useQuery({
    queryKey: ["product", storeId, productId],
    queryFn: () => fetchJson<any>(`/api/products/${productId}?storeId=${storeId}`),
    enabled: !!storeId && !!productId,
    refetchOnWindowFocus: "always",
  });
}

export function useCreateProduct(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      fetchJson(`/api/products?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productsList"] }),
  });
}

export function useUpdateProduct(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, ...data }: any) =>
      fetchJson(`/api/products/${productId}?storeId=${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["productsList"] });
      qc.invalidateQueries({ queryKey: ["product", storeId, String(variables.productId)] });
    },
  });
}

export function useDeleteProduct(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) =>
      fetchJson(`/api/products/${productId}?storeId=${storeId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productsList"] }),
  });
}

export function useProductCategories(storeId: string | null) {
  return useQuery({
    queryKey: ["productCategories", storeId],
    queryFn: () => fetchJson<any>(`/api/products/categories?storeId=${storeId}`),
    enabled: !!storeId,
  });
}

export function useCreateCategory(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; slug?: string; parent?: number; description?: string }) =>
      fetchJson(`/api/products/categories?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productCategories"] }),
  });
}

export function useUpdateCategory(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, ...data }: { categoryId: number; name?: string; slug?: string; parent?: number; description?: string }) =>
      fetchJson(`/api/products/categories/${categoryId}?storeId=${storeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productCategories"] }),
  });
}

export function useDeleteCategory(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: number) =>
      fetchJson(`/api/products/categories/${categoryId}?storeId=${storeId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["productCategories"] }),
  });
}

export function useBatchVariations(storeId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, ...batch }: { productId: number; create?: any[]; update?: any[]; delete?: number[] }) =>
      fetchJson(`/api/products/${productId}/variations/batch?storeId=${storeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["product", storeId, String(variables.productId)] });
    },
  });
}

export function useUploadProductImage(storeId: string | null) {
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return fetchJson<any>(`/api/products/media?storeId=${storeId}`, {
        method: "POST",
        body: formData,
      });
    },
  });
}

export function useDeleteProductImage(storeId: string | null) {
  return useMutation({
    mutationFn: (mediaId: number) =>
      fetchJson(`/api/products/media/${mediaId}?storeId=${storeId}`, { method: "DELETE" }),
  });
}
